import { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Logger } from 'logger'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { FormModal } from '@components/branded-modal'
import { AdasBox, AdasTextField } from '@components/wrapper-components'
import { Copy, Check, Download, Link as LinkIcon } from 'lucide-react'
import { useCenterStore, useOrganisationStore } from '@caradasstore'
import { getEmbeddedCode } from 'api/api'
import { toPng } from 'html-to-image'
import QRCode from 'qrcode.react'

const BookingLink = ({ open = true, onClose }) => {
  const organisationId = useOrganisationStore((store) => store.organisationId)
  const organisationDetails = useOrganisationStore((state) => state.organisationDetail)
  const ccId = useCenterStore((store) => store.ccId)
  const [embeddedCode, setEmbeddedCode] = useState()
  const [directLink, setDirectLink] = useState()
  // 'link' | 'image' | 'code' | null — drives the inline "Copied!" feedback on
  // each copy button. Auto-clears after ~1.5s.
  const [copied, setCopied] = useState(null)
  const flashCopied = (key) => {
    setCopied(key)
    setTimeout(() => setCopied((cur) => (cur === key ? null : cur)), 1500)
  }

  const fetchEmbeddedCode = async () => {
    const embResponse = await getEmbeddedCode({ ccId, organisationId })
    if (embResponse?.status === 200) {
      setEmbeddedCode(embResponse.data)
    } else {
      Logger.error({
        message: 'failed to fetch embedded data',
        payload: { file: 'bookingLink', method: 'getEmbeddedCode' }
      })
    }
  }

  useEffect(() => {
    if (ccId && organisationId) {
      setDirectLink(organisationDetails?.direct_link || 'https://www.schedule.caradasplus')
      fetchEmbeddedCode(ccId, organisationId)
    }
  }, [ccId, organisationId])

  const qrCodeRef = useRef(null)
  const handleDownloadImage = () => {
    if (qrCodeRef.current === null) return
    toPng(qrCodeRef.current)
      .then((dataUrl) => {
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = 'qrcode.png'
        link.click()
      })
      .catch((err) => {
        console.error('Could not generate image', err)
      })
  }
  const handleCopyImage = () => {
    if (qrCodeRef.current === null) return
    toPng(qrCodeRef.current)
      .then((dataUrl) => {
        fetch(dataUrl)
          .then((res) => res.blob())
          .then((blob) => {
            const item = new ClipboardItem({ 'image/png': blob })
            return navigator.clipboard.write([item])
          })
          .then(() => flashCopied('image'))
      })
      .catch((err) => {
        console.error('Could not copy image', err)
      })
  }

  const footer = (
    <AdasBox className='flex justify-end'>
      <button
        type='button'
        onClick={onClose}
        className='inline-flex items-center justify-center rounded-lg bg-sidebar px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-sidebar/90'
      >
        Close
      </button>
    </AdasBox>
  )

  return (
    <FormModal open={open} onClose={onClose} title='Booking Link' icon={LinkIcon} footer={footer} bodyClassName='py-5' mobileFullScreen>
      <div className='space-y-6'>
        <div>
          <div className='mb-2 text-[14px] font-bold text-[#0A1628]'>Direct Link</div>
          <AdasTextField fullWidth value={directLink} InputProps={{ readOnly: true }} variant='outlined' />
          <div className='mt-2 flex justify-end'>
            <CopyToClipboard text={directLink} onCopy={() => flashCopied('link')}>
              <button
                type='button'
                aria-live='polite'
                className='inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-[#1F6FEB] hover:text-[#1A5FD0]'
              >
                {copied === 'link' ? <Check className='h-4 w-4' /> : <Copy className='h-4 w-4' />}
                {copied === 'link' ? 'Link Copied!' : 'Copy Link'}
              </button>
            </CopyToClipboard>
          </div>
        </div>

        <div>
          <div className='mb-2 text-[14px] font-bold text-[#0A1628]'>QR Code</div>
          <AdasBox ref={qrCodeRef} className='w-fit p-1'>
            <QRCode value={directLink} size={192} />
          </AdasBox>
          <div className='mt-2 flex items-center gap-6'>
            <button
              type='button'
              onClick={handleCopyImage}
              aria-live='polite'
              className='inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-[#1F6FEB] hover:text-[#1A5FD0]'
            >
              {copied === 'image' ? <Check className='h-4 w-4' /> : <Copy className='h-4 w-4' />}
              {copied === 'image' ? 'Image Copied!' : 'Copy Image'}
            </button>
            <button
              type='button'
              onClick={handleDownloadImage}
              className='inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-[#1F6FEB] hover:text-[#1A5FD0]'
            >
              <Download className='h-4 w-4' />
              Download Image
            </button>
          </div>
        </div>

        <div>
          <div className='mb-2 text-[14px] font-bold text-[#0A1628]'>Embed Your Client</div>
          <AdasTextField fullWidth multiline minRows={5} value={embeddedCode} InputProps={{ readOnly: true }} variant='outlined' />
          <div className='mt-1 text-[12px] text-[#6B7280]'>Copy code above to embed Scheduler in your website</div>
          <div className='mt-2 flex justify-end'>
            <CopyToClipboard text={embeddedCode} onCopy={() => flashCopied('code')}>
              <button
                type='button'
                aria-live='polite'
                className='inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-[#1F6FEB] hover:text-[#1A5FD0]'
              >
                {copied === 'code' ? <Check className='h-4 w-4' /> : <Copy className='h-4 w-4' />}
                {copied === 'code' ? 'Code Copied!' : 'Copy Code'}
              </button>
            </CopyToClipboard>
          </div>
        </div>
      </div>
    </FormModal>
  )
}

BookingLink.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func
}

export default BookingLink
